import { ulid } from 'ulid';
import type { VoucherViewModel } from '@/graphql/generated';

type VoucherCloneInput = Pick<VoucherViewModel,
  | 'name'
  | 'code'
  | 'isAdHoc'
  | 'isCustomerSpecific'
  | 'tariffBindingRequired'
  | 'discountValue'
  | 'maxDiscountAmount'
  | 'usageLimit'
  | 'validUntilDate'
  | 'isActive'
  | 'campaignId'             // Foreign key - preserved (optional)
  | 'customerId'             // Foreign key - preserved (optional, for customer-specific vouchers)
  | 'facilityId'             // Foreign key - preserved (optional)
  | 'tariffId'               // Foreign key - preserved (optional, for tariff replacement vouchers)
  | 'voucherDiscountTypeId'  // Lookup - preserved
>;

/**
 * Clone a Voucher entity
 * 
 * - voucherId: NEW (regenerated)
 * - code: NEW (must generate unique code - appended with timestamp)
 * - campaignId: PRESERVED (foreign key - optional, voucher belongs to same campaign)
 * - customerId: PRESERVED (foreign key - optional, for customer-specific vouchers)
 * - facilityId: PRESERVED (foreign key - optional, voucher is for same facility)
 * - tariffId: PRESERVED (foreign key - optional, for tariff replacement vouchers)
 * - voucherDiscountTypeId: PRESERVED (lookup reference)
 * - currentUsageCount: RESET to 0 (new voucher has no uses)
 * - dateUsed: RESET to null (new voucher hasn't been used)
 * - All other fields: COPIED as-is
 */
export function cloneVoucher(source: VoucherCloneInput): VoucherCloneInput & { 
  voucherId: string;
  currentUsageCount: number;
  dateUsed: null;
} {
  // Generate a unique code by appending timestamp
  const timestamp = Date.now().toString(36).toUpperCase();
  const newCode = `${source.code}-${timestamp}`;
  
  return {
    // New entity ID
    voucherId: ulid(),
    
    // New unique code (codes must be unique)
    code: newCode,
    
    // Transform name to indicate copy
    name: source.name ? `${source.name} (Copy)` : source.name,
    
    // Preserve foreign keys
    campaignId: source.campaignId,
    customerId: source.customerId,
    facilityId: source.facilityId,
    tariffId: source.tariffId,
    
    // Preserve lookup references
    voucherDiscountTypeId: source.voucherDiscountTypeId,
    
    // Reset usage tracking for new voucher
    currentUsageCount: 0,
    dateUsed: null,
    
    // Copy voucher configuration
    isAdHoc: source.isAdHoc,
    isCustomerSpecific: source.isCustomerSpecific,
    tariffBindingRequired: source.tariffBindingRequired,
    discountValue: source.discountValue,
    maxDiscountAmount: source.maxDiscountAmount,
    usageLimit: source.usageLimit,
    validUntilDate: source.validUntilDate,
    isActive: source.isActive,
  };
}

export type ClonedVoucher = ReturnType<typeof cloneVoucher>;
